import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { resizeDoc, type SizePreset } from '@bannermatic/core';
import { DocPreview, useBuiltinFonts } from '../components/DocPreview';
import { useSession } from '../lib/session';
import { useMarketingCopy } from './content';
import { Breadcrumbs, MarketingShell } from './MarketingLayout';
import {
  aspectRatio,
  orientationOf,
  SIZE_GROUPS,
  SIZES_PATH,
  sizePath,
  sizesIn,
  templateDoc,
  templateForSize,
  TEMPLATES_PATH,
} from './programmatic';
import { useLocalePath, usePageLocale } from './PublicRoute';
import { fill } from './TemplatePages';

/** Values for the {name}, {w}, {h} and {ratio} placeholders of a size. */
export function sizeVars(p: SizePreset, name: string) {
  return { name, w: p.width, h: p.height, ratio: aspectRatio(p.width, p.height) };
}

/** Every size preset in a table, grouped by where it is used. */
export function SizesHubPage() {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const s = copy.sizes;
  const lp = useLocalePath();
  return (
    <MarketingShell>
      <section className="mx-auto max-w-5xl px-6 pt-8 pb-24">
        <Breadcrumbs items={[{ label: copy.ui.home, to: lp('/') }, { label: s.hub.label }]} />
        <div className="mt-10 inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
          {s.hub.eyebrow}
        </div>
        <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
          {s.hub.h1}
        </h1>
        <p className="mt-5 max-w-3xl text-lg text-zinc-600">{s.hub.lead}</p>
        {SIZE_GROUPS.map((group) => (
          <section key={group} className="mt-12">
            <h2 className="text-2xl font-bold tracking-tight text-zinc-900">{s.groups[group]}</h2>
            <div className="mt-4 overflow-x-auto rounded-2xl ring-1 ring-zinc-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-zinc-50 text-zinc-900">
                  <tr>
                    {s.tableHead.map((h) => (
                      <th key={h} scope="col" className="px-5 py-3 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 text-zinc-700">
                  {sizesIn(group).map((p) => (
                    <tr key={p.id}>
                      <td className="px-5 py-3">
                        <Link
                          to={lp(sizePath(p))}
                          className="font-medium text-zinc-900 hover:text-brand-700"
                        >
                          {s.presets[p.id].name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 font-mono">
                        {p.width} × {p.height}
                      </td>
                      <td className="px-5 py-3 font-mono">{aspectRatio(p.width, p.height)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </section>
    </MarketingShell>
  );
}

/** One size: dimensions, where it is used, a template laid out for it. */
export function SizePage({ preset }: { preset: SizePreset }) {
  const locale = usePageLocale();
  const copy = useMarketingCopy(locale);
  const s = copy.sizes;
  const lp = useLocalePath();
  const user = useSession((st) => st.user);
  useBuiltinFonts();
  const info = s.presets[preset.id];
  const vars = sizeVars(preset, info.name);
  const source = templateForSize(preset);
  const doc = useMemo(() => {
    const d = templateDoc(source, locale);
    return d.width === preset.width && d.height === preset.height
      ? d
      : resizeDoc(d, preset.width, preset.height);
  }, [source, locale, preset]);
  const others = sizesIn(preset.group).filter((p) => p.id !== preset.id);
  const facts: Array<[string, string]> = [
    [s.page.facts.size, `${preset.width} × ${preset.height} px`],
    [s.page.facts.ratio, vars.ratio],
    [s.page.facts.orientation, s.orientations[orientationOf(preset.width, preset.height)]],
    [s.page.facts.group, s.groups[preset.group]],
  ];

  return (
    <MarketingShell>
      <section className="mx-auto max-w-5xl px-6 pt-8 pb-24">
        <Breadcrumbs
          items={[
            { label: copy.ui.home, to: lp('/') },
            { label: s.hub.label, to: lp(SIZES_PATH) },
            { label: info.name },
          ]}
        />
        <div className="mt-10 grid items-start gap-10 lg:grid-cols-2">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-zinc-900 md:text-5xl">
              {fill(s.page.h1, vars)}
            </h1>
            <p className="mt-5 text-lg text-zinc-600">{info.text}</p>
            <h2 className="mt-8 text-lg font-bold text-zinc-900">{s.page.factsTitle}</h2>
            <dl className="mt-3 divide-y divide-zinc-100 rounded-2xl ring-1 ring-zinc-200">
              {facts.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
                  <dt className="text-zinc-500">{k}</dt>
                  <dd className="font-medium text-zinc-900">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <figure className="flex flex-col items-center gap-3 rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-6 ring-1 ring-indigo-100">
            <div className="overflow-hidden rounded-xl shadow-xl ring-1 ring-black/5">
              <DocPreview doc={doc} box={400} />
            </div>
            <figcaption className="text-center text-sm text-zinc-500">
              <span className="font-semibold text-zinc-700">{s.page.previewTitle}</span>{' '}
              {fill(s.page.previewText, vars)}
            </figcaption>
          </figure>
        </div>

        <section className="mt-16 rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 text-white shadow-xl">
          <h2 className="text-2xl font-extrabold tracking-tight">{fill(s.page.ctaTitle, vars)}</h2>
          <p className="mt-3 max-w-2xl text-indigo-50">{fill(s.page.ctaText, vars)}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={user ? '/app/templates' : '/signup'}
              className="inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-indigo-950 hover:bg-indigo-50"
            >
              {user ? copy.ui.openApp : copy.ui.startFree} <ArrowRight className="size-4" />
            </Link>
            <Link
              to={lp(TEMPLATES_PATH)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10"
            >
              {copy.ui.navTemplates}
            </Link>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="text-2xl font-bold tracking-tight text-zinc-900">{copy.ui.faqTitle}</h2>
          <div className="mt-4 divide-y divide-zinc-200">
            {s.page.faq.map(({ q, a }) => (
              <div key={q} className="py-5">
                <h3 className="font-semibold text-zinc-900">{fill(q, vars)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{fill(a, vars)}</p>
              </div>
            ))}
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-bold text-zinc-900">
              {fill(s.page.otherTitle, { group: s.groups[preset.group] })}
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={lp(sizePath(p))}
                  className="rounded-full px-3 py-1.5 text-sm text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-50 hover:text-brand-700"
                >
                  {s.presets[p.id].name}{' '}
                  <span className="font-mono text-xs text-zinc-400">
                    {p.width}×{p.height}
                  </span>
                </Link>
              ))}
              <Link
                to={lp(SIZES_PATH)}
                className="rounded-full px-3 py-1.5 text-sm font-semibold text-brand-600 ring-1 ring-brand-200 hover:bg-brand-50"
              >
                {s.hub.label}
              </Link>
            </div>
          </section>
        )}
      </section>
    </MarketingShell>
  );
}
